/**
 * AuthModal — reusable sign-in modal for AISquare apps.
 *
 * Purely presentational. Owns the UI (floating-label inputs, OAuth grid,
 * error display, animations, focus trap, keyboard handling) but delegates
 * all logic to the consumer via callbacks:
 *
 * - `onEmailLogin(email, password)` — consumer calls their login API
 * - `onOAuthClick(providerId)` — consumer builds the OAuth URL and redirects
 * - `onForgotPassword` / `onCreateAccount` — optional navigation callbacks
 *
 * Consumer also controls visibility (`open`) and passes back error/loading
 * state (`errorMessage`, `isSubmitting`). This keeps the component free of
 * Redux, API clients, env vars, and query caches.
 */
import * as React from "react";
import { useState, useRef, useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "motion/react";
import { X, Mail, Lock, Eye, EyeOff, Loader2 } from "lucide-react";
import { cn } from "../lib/cn";
import { OAuthProviderButtons } from "./OAuthProviderButtons";

/* ============================================================================
 * OAuth provider definitions — built-in icons for the four standard providers.
 * ========================================================================= */

export interface OAuthProviderConfig {
  /** Unique id passed to `onOAuthClick`. */
  id: string;
  /** Human-readable label for title/aria attributes. */
  label: string;
  /** Icon element rendered inside the button. */
  icon: React.ReactNode;
  /** Tailwind classes for resting state. */
  color: string;
  /** Tailwind classes for hover state. */
  hoverColor: string;
  /** Whether this provider is configured/available. Default true. */
  configured?: boolean;
}

const GOOGLE_ICON = (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 001 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

const GITHUB_ICON = (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
  </svg>
);

const DISCORD_ICON = (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
  </svg>
);

const MICROSOFT_ICON = (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden>
    <rect x="1" y="1" width="10" height="10" fill="#F25022" />
    <rect x="13" y="1" width="10" height="10" fill="#7FBA00" />
    <rect x="1" y="13" width="10" height="10" fill="#00A4EF" />
    <rect x="13" y="13" width="10" height="10" fill="#FFB900" />
  </svg>
);

/** Default OAuth providers. Pass a subset or custom array to `oauthProviders`. */
export const DEFAULT_OAUTH_PROVIDERS: OAuthProviderConfig[] = [
  {
    id: "google",
    label: "Google",
    icon: GOOGLE_ICON,
    color: "bg-background border border-border text-foreground",
    hoverColor: "hover:bg-muted hover:border-border",
  },
  {
    id: "github",
    label: "GitHub",
    icon: GITHUB_ICON,
    color: "bg-foreground text-background border border-foreground",
    // Keep the dark surface on hover (slightly lifted) so the light GitHub mark
    // (text-background via currentColor) stays visible. A bare opacity hover let
    // IconButton's hover:bg-accent/10 tint the surface light, hiding the icon.
    hoverColor: "hover:bg-foreground/90 hover:border-foreground/90",
  },
  {
    id: "discord",
    label: "Discord",
    icon: DISCORD_ICON,
    color: "bg-[#5865F2] text-white border border-[#5865F2]",
    hoverColor: "hover:bg-[#4752C4]",
  },
  {
    id: "microsoft",
    label: "Microsoft",
    icon: MICROSOFT_ICON,
    color: "bg-background border border-border text-foreground",
    hoverColor: "hover:bg-muted hover:border-border",
  },
];

/* ============================================================================
 * Typewriter helper — animates pasted / autofilled values character-by-character
 * for a polished UX when the browser fills credentials.
 * ========================================================================= */

function typewriteValue(
  target: string,
  setter: React.Dispatch<React.SetStateAction<string>>,
  animRef: React.MutableRefObject<ReturnType<typeof setInterval> | null>,
  targetRef: React.MutableRefObject<string | null>,
  startFrom: number,
  onTick?: (partial: string) => void,
) {
  if (animRef.current) clearInterval(animRef.current);
  targetRef.current = target;
  let i = startFrom;
  animRef.current = setInterval(() => {
    i++;
    const partial = target.slice(0, i);
    setter(partial);
    onTick?.(partial);
    if (i >= target.length) {
      clearInterval(animRef.current!);
      animRef.current = null;
      targetRef.current = null;
    }
  }, 28);
}

/* ============================================================================
 * Component
 * ========================================================================= */

export interface AuthModalProps {
  /** Controls visibility of the modal. */
  open: boolean;
  /**
   * Called when the user requests to close the modal (backdrop click, Escape,
   * close button). Ignored when `canDismiss` is false.
   */
  onClose: () => void;
  /**
   * Called when the user submits the email/password form. The consumer is
   * responsible for the actual API call and should set `errorMessage` /
   * `isSubmitting` accordingly.
   */
  onEmailLogin: (email: string, password: string) => void | Promise<void>;
  /**
   * Called when the user clicks an OAuth provider button. The consumer
   * should build the OAuth URL and redirect.
   */
  onOAuthClick?: (providerId: string) => void | Promise<void>;
  /** Called when "Forgot password?" is clicked. Optional. */
  onForgotPassword?: () => void;
  /** Called when "Create your account" is clicked. Optional. */
  onCreateAccount?: () => void;
  /**
   * Which OAuth providers to show. Defaults to Google, GitHub, Discord,
   * Microsoft. Pass an empty array to hide the OAuth section entirely.
   */
  oauthProviders?: OAuthProviderConfig[];
  /** When false, the close button is hidden and backdrop click is disabled. */
  canDismiss?: boolean;
  /** Brand logo URL shown at the top of the modal. */
  logoSrc?: string;
  /** Additional class names for the logo image element. */
  logoClassName?: string;
  /** Heading text. Default: "Sign in to AISquare". */
  title?: React.ReactNode;
  /** Subtext below the heading. Default: "Sign in to access experiences and collections". */
  description?: React.ReactNode;
  /** Error message to display. Typically set after a failed login attempt. */
  errorMessage?: string | null;
  /** Loading state. Disables inputs and shows a spinner on the submit button. */
  isSubmitting?: boolean;
  /** Mount/unmount animation. Default true. */
  animate?: boolean;
  /** URL for the Terms of Service link. Pass null to hide. */
  termsUrl?: string | null;
  /** URL for the Privacy Policy link. Pass null to hide. */
  privacyUrl?: string | null;
  /** Additional class names for the outer container. */
  className?: string;
}

export const AuthModal = React.forwardRef<HTMLDivElement, AuthModalProps>(
  (
    {
      open,
      onClose,
      onEmailLogin,
      onOAuthClick,
      onForgotPassword,
      onCreateAccount,
      oauthProviders = DEFAULT_OAUTH_PROVIDERS,
      canDismiss = true,
      logoSrc,
      logoClassName,
      title = "Sign in to AISquare",
      description = "Sign in to access experiences and collections",
      errorMessage,
      isSubmitting = false,
      animate = true,
      termsUrl,
      privacyUrl,
      className,
    },
    ref,
  ) => {
    // ── Internal form state ──────────────────────────────────────────────
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [emailFocused, setEmailFocused] = useState(false);
    const [passwordFocused, setPasswordFocused] = useState(false);
    const [internalError, setInternalError] = useState<string | null>(null);

    // Typewriter animation refs
    const emailAnimRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const pwAnimRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const emailTargetRef = useRef<string | null>(null);
    const pwTargetRef = useRef<string | null>(null);

    // Focus trap refs
    const modalRef = useRef<HTMLDivElement | null>(null);

    // Display either consumer-provided error or internal validation error
    const displayError = errorMessage ?? internalError;

    // ── Cleanup animations on unmount ────────────────────────────────────
    useEffect(() => {
      return () => {
        if (emailAnimRef.current) clearInterval(emailAnimRef.current);
        if (pwAnimRef.current) clearInterval(pwAnimRef.current);
      };
    }, []);

    // ── Reset form when modal closes ─────────────────────────────────────
    useEffect(() => {
      if (!open) {
        setEmail("");
        setPassword("");
        setShowPassword(false);
        setEmailFocused(false);
        setPasswordFocused(false);
        setInternalError(null);
        if (emailAnimRef.current) {
          clearInterval(emailAnimRef.current);
          emailAnimRef.current = null;
        }
        if (pwAnimRef.current) {
          clearInterval(pwAnimRef.current);
          pwAnimRef.current = null;
        }
        emailTargetRef.current = null;
        pwTargetRef.current = null;
      }
    }, [open]);

    // ── Keyboard: Escape to close ────────────────────────────────────────
    useEffect(() => {
      if (!open) return;
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape" && canDismiss) {
          onClose();
        }
        // Focus trap: Tab cycles within the modal
        if (e.key === "Tab" && modalRef.current) {
          const focusable = modalRef.current.querySelectorAll<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
          );
          if (focusable.length === 0) return;
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (e.shiftKey) {
            if (document.activeElement === first) {
              e.preventDefault();
              last.focus();
            }
          } else {
            if (document.activeElement === last) {
              e.preventDefault();
              first.focus();
            }
          }
        }
      };
      document.addEventListener("keydown", onKeyDown);
      return () => document.removeEventListener("keydown", onKeyDown);
    }, [open, canDismiss, onClose]);

    // ── Flush typewriter animations ──────────────────────────────────────
    const flushAnimations = useCallback(() => {
      if (emailAnimRef.current) {
        clearInterval(emailAnimRef.current);
        emailAnimRef.current = null;
      }
      if (pwAnimRef.current) {
        clearInterval(pwAnimRef.current);
        pwAnimRef.current = null;
      }
      let flushedEmail: string | undefined;
      let flushedPw: string | undefined;
      if (emailTargetRef.current !== null) {
        flushedEmail = emailTargetRef.current;
        setEmail(flushedEmail);
        emailTargetRef.current = null;
      }
      if (pwTargetRef.current !== null) {
        flushedPw = pwTargetRef.current;
        setPassword(flushedPw);
        pwTargetRef.current = null;
      }
      return { flushedEmail, flushedPw };
    }, []);

    // ── Clear validation error when user types ───────────────────────────
    const clearValidationErrorOnInput = useCallback(
      (newEmail?: string, newPassword?: string) => {
        const e = newEmail !== undefined ? newEmail : email;
        const p = newPassword !== undefined ? newPassword : password;
        const hasEmail = !!e.trim();
        const hasPassword = !!p.trim();
        if ((!hasEmail && !hasPassword) || (hasEmail && hasPassword)) {
          setInternalError(null);
        }
      },
      [email, password],
    );

    // ── Form submit ──────────────────────────────────────────────────────
    const handleSubmit = useCallback(
      async (e: React.FormEvent) => {
        e.preventDefault();

        const { flushedEmail, flushedPw } = flushAnimations();
        const finalEmail = flushedEmail ?? email;
        const finalPassword = flushedPw ?? password;

        const hasEmail = !!finalEmail.trim();
        const hasPassword = !!finalPassword.trim();
        if (!hasEmail && !hasPassword) {
          setInternalError("Please enter your email and password.");
          return;
        }
        if (!hasEmail) {
          setInternalError("Please enter your email address.");
          return;
        }
        if (!hasPassword) {
          setInternalError("Please enter your password.");
          return;
        }

        setInternalError(null);
        await onEmailLogin(finalEmail, finalPassword);
      },
      [email, password, flushAnimations, onEmailLogin],
    );

    // ── Backdrop click ───────────────────────────────────────────────────
    const handleBackdropClick = canDismiss ? onClose : undefined;

    // ── Build modal ref callback for both forwardRef and internal ref ────
    const setRefs = useCallback(
      (node: HTMLDivElement | null) => {
        modalRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      },
      [ref],
    );

    // ── Render ───────────────────────────────────────────────────────────
    const hasOAuth = oauthProviders.length > 0 && onOAuthClick;
    const showFooterLinks = onCreateAccount || termsUrl || privacyUrl;

    const modal = (
      <>
        {/* Backdrop */}
        <div
          className="fixed inset-0 z-[9998] bg-black/50 backdrop-blur-sm"
          onClick={handleBackdropClick}
          aria-hidden
        />

        {/* Modal container */}
        <div
          className={cn("fixed inset-0 z-[9999] flex items-center justify-center p-4", className)}
          onClick={handleBackdropClick}
          role="dialog"
          aria-modal="true"
          aria-label={typeof title === "string" ? title : "Sign in"}
        >
          <div
            ref={setRefs}
            className="relative w-full max-w-[420px] rounded-2xl bg-card text-card-foreground shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            {canDismiss && (
              <button
                type="button"
                onClick={onClose}
                className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            )}

            {/* Header */}
            <div className="px-8 pt-8 pb-2 text-center">
              {logoSrc ? (
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <img
                    src={logoSrc}
                    alt=""
                    aria-hidden
                    className={cn("w-full h-full object-contain", logoClassName)}
                  />
                </div>
              ) : null}
              <h2 className="text-xl font-bold text-foreground">{title}</h2>
              {description ? (
                <p className="text-sm text-muted-foreground mt-1">{description}</p>
              ) : null}
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="px-8 pt-4 pb-2 space-y-3">
              {/* Email input with floating label */}
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-[1]" />
                <label
                  className={cn(
                    "absolute left-10 text-muted-foreground pointer-events-none transition-[transform,color] duration-150",
                    emailFocused || email
                      ? "top-2.5 -translate-y-0 text-xs text-foreground"
                      : "top-1/2 -translate-y-1/2 text-sm",
                  )}
                >
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v.length - email.length > 1) {
                      setEmailFocused(true);
                      typewriteValue(
                        v,
                        setEmail,
                        emailAnimRef,
                        emailTargetRef,
                        email.length,
                        (partial) => clearValidationErrorOnInput(partial, undefined),
                      );
                    } else {
                      if (emailAnimRef.current) {
                        clearInterval(emailAnimRef.current);
                        emailAnimRef.current = null;
                        emailTargetRef.current = null;
                      }
                      setEmail(v);
                      clearValidationErrorOnInput(v, undefined);
                    }
                  }}
                  onFocus={() => setEmailFocused(true)}
                  onBlur={() => setEmailFocused(false)}
                  disabled={isSubmitting}
                  className="w-full pl-10 pr-4 pt-6 pb-2 text-sm rounded-xl border border-input bg-muted text-foreground focus:outline-none focus:ring-[1px] focus:ring-ring focus:ring-offset-0 focus:border-primary focus:bg-background transition-all disabled:opacity-60"
                  autoComplete="email"
                />
              </div>

              {/* Password input with floating label */}
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-[1]" />
                <label
                  className={cn(
                    "absolute left-10 text-muted-foreground pointer-events-none transition-[transform,color] duration-150",
                    passwordFocused || password
                      ? "top-2.5 -translate-y-0 text-xs text-foreground"
                      : "top-1/2 -translate-y-1/2 text-sm",
                  )}
                >
                  Password
                </label>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v.length - password.length > 1) {
                      setPasswordFocused(true);
                      typewriteValue(
                        v,
                        setPassword,
                        pwAnimRef,
                        pwTargetRef,
                        password.length,
                        (partial) => clearValidationErrorOnInput(undefined, partial),
                      );
                    } else {
                      if (pwAnimRef.current) {
                        clearInterval(pwAnimRef.current);
                        pwAnimRef.current = null;
                        pwTargetRef.current = null;
                      }
                      setPassword(v);
                      clearValidationErrorOnInput(undefined, v);
                    }
                  }}
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={() => setPasswordFocused(false)}
                  disabled={isSubmitting}
                  className="w-full pl-10 pr-10 pt-6 pb-2 text-sm rounded-xl border border-input bg-muted text-foreground focus:outline-none focus:ring-[1px] focus:ring-ring focus:ring-offset-0 focus:border-primary focus:bg-background transition-all disabled:opacity-60"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              {/* Error message */}
              <AnimatePresence>
                {displayError && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-xl px-3 py-2 flex items-center gap-2"
                    role="alert"
                  >
                    <span className="shrink-0" aria-hidden>
                      ⚠
                    </span>
                    <span>{displayError}</span>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Forgot password + Submit */}
              <div className="flex items-center justify-between pt-1">
                {onForgotPassword ? (
                  <button
                    type="button"
                    onClick={onForgotPassword}
                    className="text-xs text-muted-foreground hover:text-foreground font-medium transition-colors"
                  >
                    Forgot password?
                  </button>
                ) : (
                  <span />
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting || (!email.trim() && !password.trim())}
                className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold shadow-md hover:bg-primary/90 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  "Sign in"
                )}
              </button>
            </form>

            {/* OAuth section */}
            {hasOAuth && (
              <>
                {/* Divider */}
                <div className="px-8 py-3 flex items-center gap-4">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground font-medium">
                    or continue with
                  </span>
                  <div className="flex-1 h-px bg-border" />
                </div>

                {/* OAuth buttons */}
                <OAuthProviderButtons
                  className="px-8"
                  providers={oauthProviders}
                  onSelect={(id) => onOAuthClick!(id)}
                  disabled={isSubmitting}
                  showTooltip={false}
                  animated={false}
                />
              </>
            )}

            {/* Footer */}
            {showFooterLinks && (
              <div className="px-8 pt-5 pb-6 text-center space-y-3">
                {onCreateAccount && (
                  <div className="text-sm text-muted-foreground">
                    New to AISquare?{" "}
                    <button
                      type="button"
                      onClick={onCreateAccount}
                      className="text-primary hover:text-primary/90 font-semibold transition-colors"
                    >
                      Create your account
                    </button>
                  </div>
                )}
                {(termsUrl || privacyUrl) && (
                  <p className="text-[10px] text-muted-foreground leading-relaxed">
                    By signing in, you agree to our{" "}
                    {termsUrl && (
                      <a
                        href={termsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-foreground transition-colors"
                      >
                        Terms of Service
                      </a>
                    )}
                    {termsUrl && privacyUrl && " and "}
                    {privacyUrl && (
                      <a
                        href={privacyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-foreground transition-colors"
                      >
                        Privacy Policy
                      </a>
                    )}
                    .
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </>
    );

    if (animate) {
      return (
        <AnimatePresence>
          {open && (
            <>
              <motion.div
                key="auth-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                <div
                  className="fixed inset-0 z-[9998] bg-black/50 backdrop-blur-sm"
                  onClick={handleBackdropClick}
                  aria-hidden
                />
              </motion.div>
              <motion.div
                key="auth-modal"
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              >
                <div
                  className={cn(
                    "fixed inset-0 z-[9999] flex items-center justify-center p-4",
                    className,
                  )}
                  onClick={handleBackdropClick}
                  role="dialog"
                  aria-modal="true"
                  aria-label={typeof title === "string" ? title : "Sign in"}
                >
                  <div
                    ref={setRefs}
                    className="relative w-full max-w-[420px] rounded-2xl bg-card text-card-foreground shadow-2xl overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Close button */}
                    {canDismiss && (
                      <button
                        type="button"
                        onClick={onClose}
                        className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                        aria-label="Close"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}

                    {/* Header */}
                    <div className="px-8 pt-8 pb-2 text-center">
                      {logoSrc ? (
                        <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                          <img
                            src={logoSrc}
                            alt=""
                            aria-hidden
                            className={cn("w-full h-full object-contain", logoClassName)}
                          />
                        </div>
                      ) : null}
                      <h2 className="text-xl font-bold text-foreground">{title}</h2>
                      {description ? (
                        <p className="text-sm text-muted-foreground mt-1">{description}</p>
                      ) : null}
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="px-8 pt-4 pb-2 space-y-3">
                      {/* Email */}
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-[1]" />
                        <label
                          className={cn(
                            "absolute left-10 text-muted-foreground pointer-events-none transition-[transform,color] duration-150",
                            emailFocused || email
                              ? "top-2.5 -translate-y-0 text-xs text-foreground"
                              : "top-1/2 -translate-y-1/2 text-sm",
                          )}
                        >
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v.length - email.length > 1) {
                              setEmailFocused(true);
                              typewriteValue(
                                v,
                                setEmail,
                                emailAnimRef,
                                emailTargetRef,
                                email.length,
                                (partial) => clearValidationErrorOnInput(partial, undefined),
                              );
                            } else {
                              if (emailAnimRef.current) {
                                clearInterval(emailAnimRef.current);
                                emailAnimRef.current = null;
                                emailTargetRef.current = null;
                              }
                              setEmail(v);
                              clearValidationErrorOnInput(v, undefined);
                            }
                          }}
                          onFocus={() => setEmailFocused(true)}
                          onBlur={() => setEmailFocused(false)}
                          disabled={isSubmitting}
                          className="w-full pl-10 pr-4 pt-6 pb-2 text-sm rounded-xl border border-input bg-muted text-foreground focus:outline-none focus:ring-[1px] focus:ring-ring focus:ring-offset-0 focus:border-primary focus:bg-background transition-all disabled:opacity-60"
                          autoComplete="email"
                        />
                      </div>

                      {/* Password */}
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-[1]" />
                        <label
                          className={cn(
                            "absolute left-10 text-muted-foreground pointer-events-none transition-[transform,color] duration-150",
                            passwordFocused || password
                              ? "top-2.5 -translate-y-0 text-xs text-foreground"
                              : "top-1/2 -translate-y-1/2 text-sm",
                          )}
                        >
                          Password
                        </label>
                        <input
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v.length - password.length > 1) {
                              setPasswordFocused(true);
                              typewriteValue(
                                v,
                                setPassword,
                                pwAnimRef,
                                pwTargetRef,
                                password.length,
                                (partial) => clearValidationErrorOnInput(undefined, partial),
                              );
                            } else {
                              if (pwAnimRef.current) {
                                clearInterval(pwAnimRef.current);
                                pwAnimRef.current = null;
                                pwTargetRef.current = null;
                              }
                              setPassword(v);
                              clearValidationErrorOnInput(undefined, v);
                            }
                          }}
                          onFocus={() => setPasswordFocused(true)}
                          onBlur={() => setPasswordFocused(false)}
                          disabled={isSubmitting}
                          className="w-full pl-10 pr-10 pt-6 pb-2 text-sm rounded-xl border border-input bg-muted text-foreground focus:outline-none focus:ring-[1px] focus:ring-ring focus:ring-offset-0 focus:border-primary focus:bg-background transition-all disabled:opacity-60"
                          autoComplete="current-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((p) => !p)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                          tabIndex={-1}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>

                      {/* Error */}
                      <AnimatePresence>
                        {displayError && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.15 }}
                            className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-xl px-3 py-2 flex items-center gap-2"
                            role="alert"
                          >
                            <span className="shrink-0" aria-hidden>
                              ⚠
                            </span>
                            <span>{displayError}</span>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* Forgot password + Submit */}
                      <div className="flex items-center justify-between pt-1">
                        {onForgotPassword ? (
                          <button
                            type="button"
                            onClick={onForgotPassword}
                            className="text-xs text-muted-foreground hover:text-foreground font-medium transition-colors"
                          >
                            Forgot password?
                          </button>
                        ) : (
                          <span />
                        )}
                      </div>

                      <button
                        type="submit"
                        disabled={isSubmitting || (!email.trim() && !password.trim())}
                        className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold shadow-md hover:bg-primary/90 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Signing in...
                          </>
                        ) : (
                          "Sign in"
                        )}
                      </button>
                    </form>

                    {/* OAuth section */}
                    {hasOAuth && (
                      <>
                        <div className="px-8 py-3 flex items-center gap-4">
                          <div className="flex-1 h-px bg-border" />
                          <span className="text-xs text-muted-foreground font-medium">
                            or continue with
                          </span>
                          <div className="flex-1 h-px bg-border" />
                        </div>
                        <OAuthProviderButtons
                          className="px-8"
                          providers={oauthProviders}
                          onSelect={(id) => onOAuthClick!(id)}
                          disabled={isSubmitting}
                          showTooltip={false}
                          animated={false}
                        />
                      </>
                    )}

                    {/* Footer */}
                    {showFooterLinks && (
                      <div className="px-8 pt-5 pb-6 text-center space-y-3">
                        {onCreateAccount && (
                          <div className="text-sm text-muted-foreground">
                            New to AISquare?{" "}
                            <button
                              type="button"
                              onClick={onCreateAccount}
                              className="text-primary hover:text-primary/90 font-semibold transition-colors"
                            >
                              Create your account
                            </button>
                          </div>
                        )}
                        {(termsUrl || privacyUrl) && (
                          <p className="text-[10px] text-muted-foreground leading-relaxed">
                            By signing in, you agree to our{" "}
                            {termsUrl && (
                              <a
                                href={termsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline hover:text-foreground transition-colors"
                              >
                                Terms of Service
                              </a>
                            )}
                            {termsUrl && privacyUrl && " and "}
                            {privacyUrl && (
                              <a
                                href={privacyUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline hover:text-foreground transition-colors"
                              >
                                Privacy Policy
                              </a>
                            )}
                            .
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      );
    }

    // Non-animated fallback
    return open ? modal : null;
  },
);
AuthModal.displayName = "AuthModal";
